import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Room, RoomEvent, Track, VideoPresets, createLocalTracks } from 'livekit-client';
import { streamApi } from '../utils/api';
import { initSocket, joinStream, emitStreamStart, emitStreamStop, sendChatMessage, requestViewersList, emitUserHighlight } from '../utils/socket';

/**
 * Professional Streamer Dashboard with LiveKit
 * Real-time streaming with full chat support
 */
export default function StreamerPage() {
  const { slug } = useParams();
  const videoRef = useRef(null);
  const screenShareRef = useRef(null);
  const roomRef = useRef(null);
  const localTracksRef = useRef([]);
  const chatContainerRef = useRef(null);
  const chatInputRef = useRef(null);
  const videoContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  
  // User & Auth State
  const [user, setUser] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Stream Settings
  const [streamTitle, setStreamTitle] = useState('');
  const [streamCategory, setStreamCategory] = useState('');
  const [streamKey, setStreamKey] = useState('');
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [rtmpUrl, setRtmpUrl] = useState('rtmp://cineclubs.cloud/live');
  const [streamThumbnail, setStreamThumbnail] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [resolution, setResolution] = useState('1080p');
  const [fps, setFps] = useState('30');
  const [bitrate, setBitrate] = useState('4500');
  const [streamSource, setStreamSource] = useState('browser'); // 'browser' or 'obs'
  
  // Stats
  const [viewerCount, setViewerCount] = useState(0);
  const [streamDuration, setStreamDuration] = useState(0);
  const [peakViewers, setPeakViewers] = useState(0);
  
  // Media Controls
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [screenShare, setScreenShare] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  
  // UI State
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isChatFloating, setIsChatFloating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSettings, setShowSettings] = useState(false);
  const [showStreamSettings, setShowStreamSettings] = useState(false);
  const [showViewersList, setShowViewersList] = useState(false);
  const [viewersList, setViewersList] = useState([]);
  const [isPiP, setIsPiP] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [error, setError] = useState('');
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [showTimeoutDropdown, setShowTimeoutDropdown] = useState(null);
  const [highlightedUsers, setHighlightedUsers] = useState(new Set());
  const [timedOutUsers, setTimedOutUsers] = useState(new Map());
  
  // Mobile keyboard state
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const mainContentRef = useRef(null);
  
  // Chat State
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEmojis, setShowEmojis] = useState(false);
  const [highlightedMsg, setHighlightedMsg] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]); // For fullscreen overlay
  
  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Emojis
  const emojis = ['😀', '😂', '❤️', '🔥', '👏', '🎉', '😍', '🤔', '😢', '😮', '👍', '👎', '💯', '🚀', '⭐', '💎'];
  const reactions = [
    { icon: '👍', label: 'Like' },
    { icon: '👏', label: 'Clap' },
    { icon: '❤️', label: 'Love' },
    { icon: '🔥', label: 'Fire' },
    { icon: '😂', label: 'LOL' }
  ];

  // ==================== EFFECTS ====================
  
  // Load user & settings
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setStreamTitle(`${userData.username}'s Stream`);
      
      // Load saved settings from localStorage
      const savedSettings = localStorage.getItem(`stream_settings_${userData.id}`);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setResolution(settings.resolution || '1080p');
        setFps(settings.fps || '30');
        setBitrate(settings.bitrate || '4500');
        setStreamSource(settings.streamSource || 'browser');
      }
      
      // Fetch dashboard data from API
      fetchDashboardData();
      
      // Load chat messages from API
      loadChatMessages();
    } catch (err) {
      window.location.href = '/login';
    }
  }, [slug]);
  
  // Load chat messages from database
  const loadChatMessages = async () => {
    try {
      const { data } = await streamApi.getChatMessages(slug);
      if (data.success && data.messages) {
        const formattedMessages = data.messages.map(m => ({
          id: m.id,
          username: m.username,
          message: m.message,
          isStreamer: m.user_id === data.owner_id,
          timestamp: new Date(m.created_at).getTime()
        }));
        setMessages(formattedMessages);
        // Scroll to bottom after loading
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (e) {
      console.error('Failed to load chat messages:', e);
    }
  };
  
  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      const { data } = await streamApi.getDashboard();
      if (data.success) {
        if (data.stream) {
          setStreamTitle(data.stream.title || `${data.user.username}'s Stream`);
          setStreamCategory(data.stream.category || '');
          setStreamThumbnail(data.stream.thumbnail || null);
          setStreamKey(data.stream.stream_key || '');
          setRtmpUrl(data.stream.rtmp_url || 'rtmp://cineclubs.cloud/live');
          setIsLive(data.stream.status === 'live');
          setViewerCount(data.stream.viewers_count || 0);
        }
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
  };

  // Save settings when changed
  useEffect(() => {
    if (user) {
      localStorage.setItem(`stream_settings_${user.id}`, JSON.stringify({
        resolution, fps, bitrate, streamSource
      }));
    }
  }, [user, resolution, fps, bitrate, streamSource]);

  // Save messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(`chat_${slug}`, JSON.stringify(messages.slice(-200)));
    }
  }, [messages, slug]);

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Fix for mobile viewport height (accounts for browser toolbar)
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // iOS Safari keyboard fix - BULLETPROOF approach
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;
    
    const html = document.documentElement;
    const body = document.body;
    
    // Save original styles
    const originalHtmlStyle = html.getAttribute('style') || '';
    const originalBodyStyle = body.getAttribute('style') || '';
    
    // Lock html/body to kill iOS bounce completely
    html.style.cssText = 'position: fixed; overflow: hidden; width: 100%; height: 100%; top: 0; left: 0;';
    body.style.cssText = 'position: fixed; overflow: hidden; width: 100%; height: 100%; top: 0; left: 0;';
    
    // Force scroll to 0 on ANY scroll event
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener('scroll', forceScrollToTop, { passive: false });
    
    // Prevent touchmove except inside chat-messages
    const preventBounce = (e) => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages && chatMessages.contains(e.target)) {
        const scrollTop = chatMessages.scrollTop;
        const scrollHeight = chatMessages.scrollHeight;
        const clientHeight = chatMessages.clientHeight;
        
        if (scrollTop <= 0) {
          chatMessages.scrollTop = 1;
        }
        if (scrollTop + clientHeight >= scrollHeight - 1) {
          chatMessages.scrollTop = scrollHeight - clientHeight - 1;
        }
        return;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventBounce, { passive: false });
    
    // Visual Viewport API for keyboard detection
    let lastKeyboardState = false;
    
    const updateLayout = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      
      const visibleHeight = vv.height;
      window.scrollTo(0, 0);
      
      html.style.setProperty('--visible-height', `${visibleHeight}px`);
      
      const navHeight = 56;
      
      if (mainContentRef.current) {
        const contentHeight = visibleHeight - navHeight;
        mainContentRef.current.style.height = `${contentHeight}px`;
        mainContentRef.current.style.top = `${navHeight}px`;
      }
      
      const windowHeight = window.innerHeight;
      const diff = windowHeight - visibleHeight;
      const keyboardNowOpen = diff > 150;
      
      if (keyboardNowOpen !== lastKeyboardState) {
        lastKeyboardState = keyboardNowOpen;
        setIsKeyboardOpen(keyboardNowOpen);
        
        if (keyboardNowOpen && chatContainerRef.current) {
          setTimeout(() => {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }, 50);
        }
      }
    };
    
    if (window.visualViewport) {
      updateLayout();
      window.visualViewport.addEventListener('resize', updateLayout);
      window.visualViewport.addEventListener('scroll', updateLayout);
    }
    window.addEventListener('resize', updateLayout);
    
    return () => {
      window.removeEventListener('scroll', forceScrollToTop);
      document.removeEventListener('touchmove', preventBounce);
      window.removeEventListener('resize', updateLayout);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateLayout);
        window.visualViewport.removeEventListener('scroll', updateLayout);
      }
      html.setAttribute('style', originalHtmlStyle);
      body.setAttribute('style', originalBodyStyle);
    };
  }, [isMobile]);

  // Initialize Web Audio API for volume control
  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
    }
  }, []);

  // Socket connection
  useEffect(() => {
    if (!slug || !user) return;
    
    const socket = initSocket();
    joinStream(slug, user, 'streamer');
    
    socket.on('viewers:update', ({ count }) => {
      setViewerCount(count);
      if (count > peakViewers) setPeakViewers(count);
    });
    
    socket.on('viewers:list', ({ viewers }) => {
      setViewersList(viewers || []);
    });
    
    socket.on('chat:message', (msg) => {
      const newMsg = { 
        ...msg, 
        timestamp: Date.now(),
        id: Date.now() + Math.random(),
        fadeId: Date.now()
      };
      setMessages(prev => [...prev.slice(-199), newMsg]);
      
      if (!isChatOpen) setUnreadCount(c => c + 1);
      
      // Auto-scroll
      if (autoScroll) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 50);
      }
      
      // Add to recent messages for fullscreen overlay (auto-remove after 5 seconds)
      setRecentMessages(prev => [...prev.slice(-4), newMsg]);
      setTimeout(() => {
        setRecentMessages(prev => prev.filter(m => m.fadeId !== newMsg.fadeId));
      }, 5000);
      
      // Browser notification
      if (document.hidden && Notification.permission === 'granted') {
        new Notification(`${msg.username}`, { 
          body: msg.message, 
          icon: '/cinebadge.png',
          tag: 'chat-notification'
        });
      }
    });

    socket.on('gift:received', (data) => {
      addNotification(`🎁 ${data.username} sent ${data.coins} coins!`, 'gift');
    });
    
    socket.on('user:highlighted', ({ username, highlighted }) => {
      setHighlightedUsers(prev => {
        const newSet = new Set(prev);
        if (highlighted) {
          newSet.add(username);
        } else {
          newSet.delete(username);
        }
        return newSet;
      });
    });
    
    return () => {
      socket.off('viewers:update');
      socket.off('viewers:list');
      socket.off('chat:message');
      socket.off('gift:received');
      socket.off('user:highlighted');
    };
  }, [slug, user, isChatOpen, autoScroll, peakViewers]);

  // Stream duration timer
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => setStreamDuration(d => d + 1), 1000);
    return () => clearInterval(interval);
  }, [isLive]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch(e.key.toLowerCase()) {
        case 'm': toggleMic(); break;
        case 'v': toggleCamera(); break;
        case 'c': setIsChatOpen(prev => !prev); break;
        case 'escape': 
          setShowSettings(false);
          setShowStreamSettings(false);
          setShowMoreMenu(false);
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  // ==================== HELPER FUNCTIONS ====================
  
  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const generateStreamKey = async () => {
    try {
      const { data } = await streamApi.generateStreamKey();
      if (data.success) {
        setStreamKey(data.stream_key);
        setRtmpUrl(data.rtmp_url);
        addNotification('Stream key generated!', 'success');
      } else {
        addNotification('Failed to generate key', 'error');
      }
    } catch (err) {
      console.error('Generate stream key error:', err);
      addNotification('Failed to generate key', 'error');
    }
  };
  
  // Copy stream key to clipboard
  const copyStreamKey = () => {
    navigator.clipboard.writeText(streamKey);
    addNotification('Stream key copied!', 'success');
  };
  
  // Copy RTMP URL to clipboard
  const copyRtmpUrl = () => {
    navigator.clipboard.writeText(rtmpUrl);
    addNotification('RTMP URL copied!', 'success');
  };
  
  // Save stream settings to API
  const saveStreamSettings = async () => {
    try {
      const { data } = await streamApi.updateSettings({
        title: streamTitle,
        category: streamCategory,
        thumbnail: streamThumbnail
      });
      if (data.success) {
        addNotification('Settings saved!', 'success');
        setShowStreamSettings(false);
      } else {
        addNotification('Failed to save settings', 'error');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      addNotification('Failed to save settings', 'error');
    }
  };

  // ==================== LIVEKIT FUNCTIONS ====================
  
  const LIVEKIT_URL = 'wss://cineclubs.cloud:7882';
  
  const startPreview = async () => {
    try {
      // Create local tracks for preview
      const tracks = await createLocalTracks({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: {
          resolution: resolution === '1080p' ? VideoPresets.h1080.resolution : 
                      resolution === '720p' ? VideoPresets.h720.resolution : 
                      VideoPresets.h540.resolution,
          facingMode: 'user'
        }
      });
      
      localTracksRef.current = tracks;
      
      // Attach video to preview
      const videoTrack = tracks.find(t => t.kind === 'video');
      if (videoTrack && videoRef.current) {
        videoTrack.attach(videoRef.current);
      }
      
      setCameraEnabled(true);
      setMicEnabled(true);
    } catch (err) {
      console.error('Preview error:', err);
      addNotification('Camera access denied', 'error');
    }
  };

  const handleGoLive = async () => {
    if (isConnecting) return;
    setIsConnecting(true);
    
    try {
      // Start preview if not already started
      if (localTracksRef.current.length === 0) {
        await startPreview();
      }
      
      // Call API to mark stream as LIVE in database
      await streamApi.start(streamTitle);
      
      // Get LiveKit token
      const { data } = await streamApi.getLivekitToken(slug, true);
      if (!data.success || !data.token) {
        throw new Error('Failed to get stream token');
      }
      
      // Create LiveKit room with reconnection
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        reconnectPolicy: {
          maxRetries: 10,
          nextRetryDelayInMs: (retryCount) => Math.min(1000 * Math.pow(2, retryCount), 30000)
        },
        videoCaptureDefaults: {
          resolution: resolution === '1080p' ? VideoPresets.h1080.resolution :
                      resolution === '720p' ? VideoPresets.h720.resolution :
                      VideoPresets.h540.resolution
        }
      });
      
      // Connect to LiveKit
      await room.connect(LIVEKIT_URL, data.token);
      roomRef.current = room;
      console.log('✅ Connected to LiveKit');
      
      // Publish local tracks
      for (const track of localTracksRef.current) {
        await room.localParticipant.publishTrack(track, {
          simulcast: true
        });
      }
      
      // Room events - Real viewer count from LiveKit
      room.on(RoomEvent.ParticipantConnected, () => {
        const realCount = room.remoteParticipants.size;
        setViewerCount(realCount);
        if (realCount > peakViewers) {
          setPeakViewers(realCount);
        }
      });
      
      room.on(RoomEvent.ParticipantDisconnected, () => {
        setViewerCount(room.remoteParticipants.size);
      });
      
      room.on(RoomEvent.Disconnected, (reason) => {
        console.log('🔌 Disconnected from LiveKit:', reason);
        // Only end stream if it was intentional or final
        if (reason === 'ROOM_DELETED' || reason === 'PARTICIPANT_REMOVED') {
          handleStreamEnd();
        } else {
          addNotification('Connection lost - attempting to reconnect...', 'warning');
        }
      });
      
      room.on(RoomEvent.Reconnecting, () => {
        console.log('🔄 Reconnecting to LiveKit...');
        addNotification('Reconnecting...', 'warning');
      });
      
      room.on(RoomEvent.Reconnected, () => {
        console.log('✅ Reconnected to LiveKit');
        addNotification('Reconnected!', 'success');
      });
      
      // Set initial viewer count
      setViewerCount(room.remoteParticipants.size);
      
      setIsLive(true);
      setStreamDuration(0);
      emitStreamStart(slug);
      addNotification('🔴 You are now LIVE!', 'success');
      
    } catch (err) {
      console.error('Stream start error:', err);
      addNotification(err.message || 'Connection failed', 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  // End stream helper function
  const handleStreamEnd = async () => {
    try {
      // Call API to mark stream as offline
      await streamApi.stop();
    } catch (e) {
      console.error('Failed to mark stream offline:', e);
    }
    
    setIsLive(false);
    emitStreamStop(slug);
  };

  const handleEndStream = async () => {
    if (!window.confirm('Are you sure you want to end the stream?')) return;
    
    try {
      // Disconnect from LiveKit
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
      
      // Stop local tracks completely
      localTracksRef.current.forEach(track => {
        track.stop();
        track.detach();
      });
      localTracksRef.current = [];
      
      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Call API to mark stream as offline in database
      await handleStreamEnd();
      
      setCameraEnabled(false);
      setMicEnabled(false);
      setViewerCount(0);
      addNotification('Stream ended', 'info');
      
    } catch (err) {
      console.error('End stream error:', err);
    }
  };
  
  // Warn before leaving page (but don't auto-end stream)
  useEffect(() => {
    if (!isLive) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your stream is still live!';
      return e.returnValue;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLive]);

  const toggleCamera = async () => {
    if (roomRef.current && isLive) {
      const enabled = roomRef.current.localParticipant.isCameraEnabled;
      await roomRef.current.localParticipant.setCameraEnabled(!enabled);
      setCameraEnabled(!enabled);
    } else {
      // Preview mode
      const videoTrack = localTracksRef.current.find(t => t.kind === 'video');
      if (videoTrack) {
        setCameraEnabled(prev => {
          if (prev) {
            videoTrack.mute();
          } else {
            videoTrack.unmute();
          }
          return !prev;
        });
      } else {
        setCameraEnabled(prev => !prev);
      }
    }
  };

  const toggleMic = async () => {
    if (roomRef.current && isLive) {
      const enabled = roomRef.current.localParticipant.isMicrophoneEnabled;
      await roomRef.current.localParticipant.setMicrophoneEnabled(!enabled);
      setMicEnabled(!enabled);
    } else {
      // Preview mode  
      const audioTrack = localTracksRef.current.find(t => t.kind === 'audio');
      if (audioTrack) {
        setMicEnabled(prev => {
          if (prev) {
            audioTrack.mute();
          } else {
            audioTrack.unmute();
          }
          return !prev;
        });
      } else {
        setMicEnabled(prev => !prev);
      }
    }
  };

  const toggleScreenShare = async () => {
    if (!roomRef.current || !isLive) {
      addNotification('Go live first to share screen', 'info');
      return;
    }
    
    try {
      const enabled = roomRef.current.localParticipant.isScreenShareEnabled;
      
      if (!enabled) {
        // Start screen share
        await roomRef.current.localParticipant.setScreenShareEnabled(true);
        setScreenShare(true);
        
        // Get screen share track for mirror preview
        setTimeout(() => {
          const screenTrack = roomRef.current.localParticipant.getTrackPublication('screen_share')?.track;
          if (screenTrack && screenShareRef.current) {
            screenTrack.attach(screenShareRef.current);
          }
        }, 500);
      } else {
        // Stop screen share
        await roomRef.current.localParticipant.setScreenShareEnabled(false);
        setScreenShare(false);
        
        // Detach from preview
        if (screenShareRef.current) {
          screenShareRef.current.srcObject = null;
        }
      }
    } catch (err) {
      console.log('Screen share cancelled');
      setScreenShare(false);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
    
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.setValueAtTime(val, audioContextRef.current.currentTime);
    }
  };

  const togglePiP = async () => {
    if (videoRef.current) {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        await videoRef.current.requestPictureInPicture();
        setIsPiP(true);
      }
    }
  };

  // Fullscreen toggle - works on both mobile and desktop
  const toggleFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    
    // Check if already in fullscreen
    const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || (video && video.webkitDisplayingFullscreen));
    
    if (!isCurrentlyFullscreen) {
      // ENTER FULLSCREEN
      
      // iOS Safari - use native video fullscreen
      if (video && typeof video.webkitEnterFullscreen === 'function') {
        try {
          // Make sure video is playing first (required for iOS)
          if (video.paused) {
            await video.play().catch(() => {});
          }
          video.webkitEnterFullscreen();
          return;
        } catch (e) {
          console.log('iOS fullscreen failed, trying container');
        }
      }
      
      // Desktop/Android - use container fullscreen
      if (container) {
        if (container.requestFullscreen) {
          await container.requestFullscreen().catch(() => {});
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      }
    } else {
      // EXIT FULLSCREEN
      if (document.exitFullscreen) {
        await document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      const video = videoRef.current;
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || (video && video.webkitDisplayingFullscreen));
      setIsFullscreen(isFs);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    // iOS video fullscreen events
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);
    }
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', handleFullscreenChange);
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange);
      }
    };
  }, []);

  // ==================== CHAT FUNCTIONS ====================
  
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    
    sendChatMessage(chatInput.trim());
    setChatInput('');
    setShowEmojis(false);
  };

  const addEmoji = (emoji) => {
    setChatInput(prev => prev + emoji);
  };

  const handleToggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) setUnreadCount(0);
  };

  const deleteMessage = (msgId) => {
    setMessages(prev => prev.filter(m => m.id !== msgId));
    addNotification('Message deleted', 'info');
  };

  const highlightMessage = (msgId) => {
    setHighlightedMsg(msgId);
    addNotification('Message highlighted', 'info');
  };

  // Viewer moderation functions
  const toggleHighlightUser = (username) => {
    const isHighlighted = highlightedUsers.has(username);
    setHighlightedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(username)) {
        newSet.delete(username);
        addNotification(`${username} unhighlighted`, 'info');
      } else {
        newSet.add(username);
        addNotification(`${username} highlighted`, 'success');
      }
      return newSet;
    });
    // Broadcast to all viewers
    emitUserHighlight(username, !isHighlighted);
    setSelectedViewer(null);
  };

  const timeoutUser = (username, minutes) => {
    const expiry = Date.now() + (minutes * 60 * 1000);
    setTimedOutUsers(prev => new Map(prev).set(username, expiry));
    addNotification(`${username} timed out for ${minutes} min`, 'warning');
    setShowTimeoutDropdown(null);
    setSelectedViewer(null);
  };

  const deleteUserMessages = (username) => {
    setMessages(prev => prev.filter(m => m.username !== username));
    addNotification(`All messages from ${username} deleted`, 'info');
    setSelectedViewer(null);
  };

  // Check if user is timed out
  const isUserTimedOut = (username) => {
    const expiry = timedOutUsers.get(username);
    if (!expiry) return false;
    if (Date.now() > expiry) {
      // Remove expired timeout
      setTimedOutUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(username);
        return newMap;
      });
      return false;
    }
    return true;
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      addNotification('Please select an image file', 'error');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      addNotification('Image must be less than 5MB', 'error');
      return;
    }
    
    // Preview immediately
    const reader = new FileReader();
    reader.onload = (event) => {
      setStreamThumbnail(event.target.result);
    };
    reader.readAsDataURL(file);
    
    // Upload to server
    try {
      const { data } = await streamApi.uploadThumbnail(file);
      if (data.success && data.url) {
        setStreamThumbnail(data.url);
        addNotification('Thumbnail uploaded!', 'success');
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      addNotification('Upload failed, using local preview', 'info');
    }
  };

  // Start preview on mount
  useEffect(() => {
    startPreview();
    return () => {
      // Cleanup LiveKit
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
      // Stop local tracks
      localTracksRef.current.forEach(track => track.stop());
    };
  }, []);

  // ==================== RENDER ====================
  
  return (
    <div className="streamer-page">
      {/* Notifications Toast */}
      <div className="notifications-container">
        {notifications.map(n => (
          <div key={n.id} className={`notification ${n.type}`}>
            {n.message}
          </div>
        ))}
      </div>

      {/* Top Navigation */}
      <nav className="top-nav">
        <div className="nav-left">
          <a href="/live/streams" className="logo">
            <img src="/public/assets/images/cineclubs.png" alt="CineClubs" />
            <div className="logo-text">
              <span className="logo-title"><span className="text-white">CINE</span><span className="text-red">CLUBS</span></span>
              <span className="logo-subtitle">Your Private Cinema</span>
            </div>
          </a>
          
          {isLive && (
            <div className="live-indicator">
              <span className="pulse-dot"></span>
              <span>LIVE</span>
              <span className="duration">{formatDuration(streamDuration)}</span>
            </div>
          )}
        </div>
        
        <div className="nav-center">
          <div className="title-wrapper">
            <i className="fas fa-pen"></i>
            <input
              type="text"
              value={streamTitle}
              onChange={(e) => setStreamTitle(e.target.value)}
              placeholder="Enter stream title..."
              disabled={isLive}
              maxLength={100}
            />
          </div>
        </div>
        
        <div className="nav-right">
          <div 
            className="stat-item clickable"
            onClick={() => {
              requestViewersList();
              setShowViewersList(true);
            }}
            title="View all viewers"
          >
            <i className="fas fa-eye"></i>
            <span>{viewerCount}</span>
          </div>
          
          <div className="stat-item peak">
            <i className="fas fa-chart-line"></i>
            <span>{peakViewers}</span>
          </div>
          
          <button 
            className="nav-btn"
            onClick={() => setShowStreamSettings(true)}
            title="Stream Settings (OBS)"
          >
            <i className="fas fa-broadcast-tower"></i>
          </button>
          
          <button 
            className="nav-btn"
            onClick={() => setShowSettings(true)}
            title="Settings"
          >
            <i className="fas fa-cog"></i>
          </button>
          
          <button 
            className={`nav-btn chat-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
            onClick={handleToggleChat}
            title="Toggle Chat (C)"
          >
            <i className="fas fa-comments"></i>
            {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main ref={mainContentRef} className={`main-content ${isChatOpen && !isChatFloating ? 'with-chat' : ''} ${isMobile ? 'mobile-layout' : ''} ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
        <section className="video-section">
          {/* Video Container */}
          <div className="video-container" ref={videoContainerRef}>
            {/* Screen Share Mirror (Picture-in-Picture style) */}
            {screenShare && (
              <div className="screen-share-mirror">
                <video
              ref={screenShareRef}
              autoPlay
              muted
              playsInline
            />
            <div className="mirror-label">
              <i className="fas fa-desktop"></i> Screen Share
            </div>
          </div>
        )}
        
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controls={false}
          className={`preview-video ${!cameraEnabled ? 'hidden' : ''} ${screenShare ? 'with-screen-share' : ''}`}
          style={{ WebkitMediaControls: 'none' }}
        />
        
        {!cameraEnabled && (
          <div className="camera-off">
            <div className="camera-icon">
              <i className="fas fa-video-slash"></i>
            </div>
            <p>Camera is off</p>
          </div>
        )}
        
        {/* Live Stats Overlay */}
        {isLive && (
          <div className="live-overlay">
            <div className="overlay-left">
              <div className="stat">
                <i className="fas fa-clock"></i>
                {formatDuration(streamDuration)}
              </div>
            </div>
            
            <div className="overlay-right">
              {screenShare && (
                <div className="stat screen-share">
                  <i className="fas fa-desktop"></i>
                  Sharing Screen
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Fullscreen Chat Overlay */}
        {isFullscreen && recentMessages.length > 0 && (
          <div className="fullscreen-chat-overlay">
            {recentMessages.map((msg) => (
              <div key={msg.fadeId || msg.id} className="overlay-message">
                <span className="overlay-username">{msg.username}</span>
                <span className="overlay-text">{msg.message}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Video Controls */}
        <div className="video-controls">
          <div className="controls-left">
            <button 
              className={`ctrl-btn ${!micEnabled ? 'off' : ''}`}
              onClick={toggleMic}
              title={micEnabled ? 'Mute (M)' : 'Unmute (M)'}
            >
              <i className={`fas ${micEnabled ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
            </button>
                
                <button 
                  className={`ctrl-btn ${!cameraEnabled ? 'off' : ''}`}
                  onClick={toggleCamera}
                  title={cameraEnabled ? 'Turn off camera (V)' : 'Turn on camera (V)'}
                >
                  <i className={`fas ${cameraEnabled ? 'fa-video' : 'fa-video-slash'}`}></i>
                </button>
                
                {!isMobile && (
                  <button 
                    className={`ctrl-btn ${screenShare ? 'active' : ''}`}
                    onClick={toggleScreenShare}
                    title="Share Screen"
                  >
                    <i className="fas fa-desktop"></i>
                  </button>
                )}
                
                <div className="volume-control">
                  <button 
                    className="ctrl-btn small"
                    onClick={() => setIsMuted(!isMuted)}
                    title="Monitor Audio"
                  >
                    <i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}></i>
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                </div>
              </div>
              
              <div className="controls-center">
                {!isLive ? (
                  <button 
                    className="go-live-btn"
                    onClick={handleGoLive}
                    disabled={isConnecting}
                  >
                    {isConnecting ? (
                      <>
                        <div className="spinner"></div>
                        <span>Starting...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-broadcast-tower"></i>
                        <span>Go Live</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button className="end-btn" onClick={handleEndStream}>
                    <i className="fas fa-stop-circle"></i>
                    <span>End Stream</span>
                  </button>
                )}
              </div>
              
              <div className="controls-right">
                {!isMobile && (
                  <>
                    <button 
                      className={`ctrl-btn ${isPiP ? 'active' : ''}`}
                      onClick={togglePiP}
                      title="Picture in Picture"
                    >
                      <i className="fas fa-external-link-square-alt"></i>
                    </button>
                    
                    <button 
                      className={`ctrl-btn ${isChatFloating ? 'active' : ''}`}
                      onClick={() => setIsChatFloating(!isChatFloating)}
                      title="Float Chat"
                    >
                      <i className="fas fa-window-restore"></i>
                    </button>
                  </>
                )}
                
                <button 
                  className="ctrl-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                </button>
                
                <div className="more-menu-wrapper">
                  <button 
                    className="ctrl-btn"
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    title="More"
                  >
                    <i className="fas fa-ellipsis-v"></i>
                  </button>
                  
                  {showMoreMenu && (
                    <div className="more-menu">
                      <button onClick={() => { navigator.clipboard.writeText(`https://cineclubs.cloud/live/${slug}`); addNotification('Link copied!', 'success'); setShowMoreMenu(false); }}>
                        <i className="fas fa-link"></i> Copy Link
                      </button>
                      <button onClick={() => { setShowStreamSettings(true); setShowMoreMenu(false); }}>
                        <i className="fas fa-broadcast-tower"></i> OBS Settings
                      </button>
                      <button onClick={() => { setShowSettings(true); setShowMoreMenu(false); }}>
                        <i className="fas fa-sliders-h"></i> Preferences
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Stream Info Bar */}
          <div className="stream-info">
            <div className="streamer-info">
              <img src={user?.avatar || '/cinebadge.png'} alt="" className="avatar" />
              <div className="info-text">
                <h3>{user?.username}</h3>
                <span className={`status ${isLive ? 'live' : ''}`}>
                  {isLive ? '🔴 Currently Live' : '⚫ Offline'}
                </span>
              </div>
            </div>
            
            <div className="stream-actions">
              <button 
                className="action-btn"
                onClick={() => navigator.clipboard.writeText(`https://cineclubs.cloud/live/${slug}`)}
                title="Copy stream link"
              >
                <i className="fas fa-share-alt"></i>
                <span>Share</span>
              </button>
              
              <button 
                className="action-btn"
                onClick={() => setShowStreamSettings(true)}
                title="Stream settings"
              >
                <i className="fas fa-cog"></i>
                <span>Settings</span>
              </button>
            </div>
          </div>
        </section>

        {/* Chat Section */}
        <aside className={`chat-section ${isChatOpen ? 'open' : 'closed'} ${isChatFloating ? 'floating' : ''} ${isMobile ? 'mobile' : ''}`}>
          <div className="chat-header">
            <div className="chat-tabs">
              <button 
                className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => setActiveTab('chat')}
              >
                <i className="fas fa-comments"></i> Chat
              </button>
              <button 
                className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                <i className="fas fa-bolt"></i> Activity
              </button>
            </div>
            
            <div className="chat-actions">
              <button 
                className={`small-btn ${autoScroll ? 'active' : ''}`}
                onClick={() => setAutoScroll(!autoScroll)}
                title={autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
              >
                <i className="fas fa-arrow-down"></i>
              </button>
              {!isMobile && (
                <button 
                  className="small-btn"
                  onClick={() => setIsChatFloating(!isChatFloating)}
                  title="Float chat"
                >
                  <i className="fas fa-external-link-alt"></i>
                </button>
              )}
              {!isMobile && (
                <button 
                  className="small-btn close"
                  onClick={() => setIsChatOpen(false)}
                  title="Close chat"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          
          <div className="chat-messages" ref={chatContainerRef}>
            {messages.length === 0 ? (
              <div className="chat-empty">
                <i className="fas fa-comment-dots"></i>
                <p>No messages yet</p>
                <span>Chat messages will appear here</span>
              </div>
            ) : (
              messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`message ${msg.isStreamer ? 'streamer' : ''} ${highlightedMsg === msg.id ? 'highlighted' : ''} ${highlightedUsers.has(msg.username) ? 'user-highlighted' : ''}`}
                >
                  <div className="msg-header">
                    <span className="msg-badges">
                      {msg.isStreamer && <span className="badge-icon host">🎬</span>}
                      {msg.isMod && <span className="badge-icon mod">🛡️</span>}
                      {msg.isVip && <span className="badge-icon vip">⭐</span>}
                      {highlightedUsers.has(msg.username) && <span className="badge-icon highlight">✨</span>}
                    </span>
                    <span className={`msg-author ${highlightedUsers.has(msg.username) ? 'highlighted-user' : ''}`}>{msg.username}</span>
                    <span className="msg-time">{formatTime(msg.timestamp)}</span>
                  </div>
                  <div className="msg-content">{msg.message}</div>
                  
                  {/* Admin Controls */}
                  <div className="msg-actions">
                    <button onClick={() => highlightMessage(msg.id)} title="Highlight">
                      <i className="fas fa-star"></i>
                    </button>
                    <button onClick={() => deleteMessage(msg.id)} title="Delete">
                      <i className="fas fa-trash"></i>
                    </button>
                    <button onClick={() => toggleHighlightUser(msg.username)} title="Highlight user">
                      <i className={`fas fa-user-tag ${highlightedUsers.has(msg.username) ? 'active' : ''}`}></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Reactions Bar */}
          <div className="reactions-bar">
            {reactions.map((r, i) => (
              <button 
                key={i}
                className="reaction-btn"
                onClick={() => sendChatMessage(r.icon)}
                title={r.label}
              >
                {r.icon}
              </button>
            ))}
          </div>
          
          {/* Chat Input */}
          <form className="chat-form" onSubmit={handleSendMessage}>
            <div className="input-wrapper">
              <button 
                type="button"
                className="emoji-btn"
                onClick={() => setShowEmojis(!showEmojis)}
              >
                <i className="fas fa-smile"></i>
              </button>
              
              <input
                ref={chatInputRef}
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Send a message..."
                maxLength={500}
                onFocus={() => isMobile && setIsKeyboardOpen(true)}
                onBlur={() => isMobile && setTimeout(() => setIsKeyboardOpen(false), 100)}
              />
              
              <button type="submit" className="send-btn" disabled={!chatInput.trim()}>
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
            
            {/* Emoji Picker */}
            {showEmojis && (
              <div className="emoji-picker">
                {emojis.map((e, i) => (
                  <button key={i} type="button" onClick={() => addEmoji(e)}>{e}</button>
                ))}
              </div>
            )}
          </form>
        </aside>
      </main>

      {/* Stream Settings Modal (OBS) */}
      {showStreamSettings && (
        <div className="modal-overlay" onClick={() => setShowStreamSettings(false)}>
          <div className="modal obs-settings" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-broadcast-tower"></i> Stream Settings</h2>
              <button onClick={() => setShowStreamSettings(false)}><i className="fas fa-times"></i></button>
            </div>
            
            <div className="modal-content">
              {/* Stream Source Toggle */}
              <div className="setting-group">
                <label>Stream Source</label>
                <div className="source-toggle">
                  <button 
                    className={`source-btn ${streamSource === 'browser' ? 'active' : ''}`}
                    onClick={() => setStreamSource('browser')}
                  >
                    <i className="fas fa-video"></i>
                    <span>Browser</span>
                    <small>Use webcam</small>
                  </button>
                  <button 
                    className={`source-btn ${streamSource === 'obs' ? 'active' : ''}`}
                    onClick={() => setStreamSource('obs')}
                  >
                    <i className="fas fa-desktop"></i>
                    <span>OBS / RTMP</span>
                    <small>External software</small>
                  </button>
                </div>
              </div>
              
              {/* Stream Info */}
              <div className="setting-group">
                <label>Stream Title</label>
                <input
                  type="text"
                  value={streamTitle}
                  onChange={(e) => setStreamTitle(e.target.value)}
                  placeholder="Enter stream title..."
                  maxLength={100}
                />
              </div>
              
              <div className="setting-group">
                <label>Category</label>
                <select value={streamCategory} onChange={(e) => setStreamCategory(e.target.value)}>
                  <option value="">Select a category</option>
                  <option value="Just Chatting">Just Chatting</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Music">Music</option>
                  <option value="Movies & Shows">Movies & Shows</option>
                  <option value="Art & Creative">Art & Creative</option>
                  <option value="Education">Education</option>
                  <option value="Tech">Tech</option>
                  <option value="Sports">Sports</option>
                  <option value="Cooking">Cooking</option>
                  <option value="Travel">Travel</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* OBS Settings - Only show when OBS is selected */}
              {streamSource === 'obs' && (
                <>
                  <div className="setting-group">
                    <label>RTMP Server URL</label>
                    <div className="key-input">
                      <input
                        type="text"
                        value={rtmpUrl}
                        readOnly
                      />
                      <button type="button" onClick={copyRtmpUrl} title="Copy URL">
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="setting-group">
                    <label>Stream Key</label>
                    <div className="key-input">
                      <input
                        type={showStreamKey ? 'text' : 'password'}
                        value={streamKey || 'Click generate to create key'}
                        readOnly
                        placeholder="Generate a stream key"
                      />
                      <button type="button" onClick={() => setShowStreamKey(!showStreamKey)} title="Show/Hide">
                        <i className={`fas ${showStreamKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                      <button type="button" onClick={copyStreamKey} title="Copy Key" disabled={!streamKey}>
                        <i className="fas fa-copy"></i>
                      </button>
                      <button type="button" onClick={generateStreamKey} title="Generate New Key">
                        <i className="fas fa-sync"></i>
                      </button>
                    </div>
                    <small className="warning-text">⚠️ Keep this key private! Anyone with this key can stream to your channel.</small>
                  </div>
                  
                  <div className="obs-instructions">
                    <h4><i className="fas fa-info-circle"></i> OBS Setup Instructions</h4>
                    <ol>
                      <li>Open OBS Studio</li>
                      <li>Go to Settings → Stream</li>
                      <li>Select "Custom..." as Service</li>
                      <li>Paste the Server URL: <code>{rtmpUrl}</code></li>
                      <li>Paste your Stream Key</li>
                      <li>Click Apply and Start Streaming!</li>
                    </ol>
                  </div>
                </>
              )}
              
              {/* Browser Settings - Only show when Browser is selected */}
              {streamSource === 'browser' && (
                <>
                  <div className="settings-row">
                    <div className="setting-group half">
                      <label>Resolution</label>
                      <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                        <option value="1080p">1080p (Full HD)</option>
                        <option value="720p">720p (HD)</option>
                        <option value="480p">480p (SD)</option>
                      </select>
                    </div>
                    
                    <div className="setting-group half">
                      <label>Frame Rate</label>
                      <select value={fps} onChange={(e) => setFps(e.target.value)}>
                        <option value="60">60 FPS</option>
                        <option value="30">30 FPS</option>
                        <option value="24">24 FPS</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="setting-group">
                    <label>Bitrate (kbps)</label>
                    <input
                      type="range"
                      min="1000"
                      max="8000"
                      step="500"
                      value={bitrate}
                      onChange={(e) => setBitrate(e.target.value)}
                    />
                    <div className="range-labels">
                      <span>1000</span>
                      <span className="current">{bitrate} kbps</span>
                      <span>8000</span>
                    </div>
                  </div>
                </>
              )}
              
              {/* Thumbnail */}
              <div className="setting-group">
                <label>Stream Thumbnail</label>
                <div className="thumbnail-upload">
                  {streamThumbnail ? (
                    <div className="thumb-preview">
                      <img src={streamThumbnail} alt="Thumbnail" />
                      <button onClick={() => setStreamThumbnail(null)}><i className="fas fa-times"></i></button>
                    </div>
                  ) : (
                    <label className="upload-btn">
                      <i className="fas fa-image"></i>
                      <span>Upload Thumbnail</span>
                      <small>Recommended: 1280x720 (16:9)</small>
                      <input type="file" accept="image/*" onChange={handleThumbnailUpload} hidden />
                    </label>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn secondary" onClick={() => setShowStreamSettings(false)}>Cancel</button>
              <button className="btn primary" onClick={saveStreamSettings}>Save Settings</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal settings-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-cog"></i> Preferences</h2>
              <button onClick={() => setShowSettings(false)}><i className="fas fa-times"></i></button>
            </div>
            
            <div className="modal-content">
              <div className="setting-item">
                <div className="setting-info">
                  <span>Camera</span>
                  <small>Toggle your camera on/off</small>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={cameraEnabled} onChange={toggleCamera} />
                  <span className="slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span>Microphone</span>
                  <small>Toggle your microphone on/off</small>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={micEnabled} onChange={toggleMic} />
                  <span className="slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span>Auto-scroll Chat</span>
                  <small>Automatically scroll to new messages</small>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={autoScroll} onChange={() => setAutoScroll(!autoScroll)} />
                  <span className="slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span>Floating Chat</span>
                  <small>Overlay chat on the video</small>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={isChatFloating} onChange={() => setIsChatFloating(!isChatFloating)} />
                  <span className="slider"></span>
                </label>
              </div>
              
              <div className="shortcuts-section">
                <h4>Keyboard Shortcuts</h4>
                <div className="shortcut"><kbd>M</kbd> Toggle Microphone</div>
                <div className="shortcut"><kbd>V</kbd> Toggle Camera</div>
                <div className="shortcut"><kbd>C</kbd> Toggle Chat</div>
                <div className="shortcut"><kbd>F</kbd> Toggle Fullscreen</div>
                <div className="shortcut"><kbd>ESC</kbd> Close Panels</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Viewers List Modal */}
      {showViewersList && (
        <div className="modal-overlay" onClick={() => { setShowViewersList(false); setSelectedViewer(null); setShowTimeoutDropdown(null); }}>
          <div className="modal viewers-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-users"></i> Viewers ({viewerCount})</h2>
              <button onClick={() => { setShowViewersList(false); setSelectedViewer(null); }}><i className="fas fa-times"></i></button>
            </div>
            
            <div className="modal-content">
              {viewersList.length === 0 ? (
                <div className="no-viewers">
                  <i className="fas fa-user-slash"></i>
                  <p>No viewers yet</p>
                </div>
              ) : (
                <div className="viewers-list">
                  {viewersList.map((viewer, idx) => (
                    <div 
                      key={idx} 
                      className={`viewer-item ${highlightedUsers.has(viewer.username) ? 'highlighted' : ''} ${isUserTimedOut(viewer.username) ? 'timed-out' : ''}`}
                      onClick={() => setSelectedViewer(selectedViewer === viewer.username ? null : viewer.username)}
                    >
                      <div className="viewer-avatar">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="viewer-info">
                        <span className="viewer-name">{viewer.username}</span>
                        {highlightedUsers.has(viewer.username) && <span className="badge highlight-badge">⭐</span>}
                        {isUserTimedOut(viewer.username) && <span className="badge timeout-badge">🔇</span>}
                      </div>
                      
                      {selectedViewer === viewer.username && (
                        <div className="viewer-actions" onClick={e => e.stopPropagation()}>
                          <button 
                            className={`action-btn ${highlightedUsers.has(viewer.username) ? 'active' : ''}`}
                            onClick={() => toggleHighlightUser(viewer.username)}
                            title="Highlight"
                          >
                            <i className="fas fa-star"></i>
                          </button>
                          
                          <div className="timeout-wrapper">
                            <button 
                              className={`action-btn ${isUserTimedOut(viewer.username) ? 'active' : ''}`}
                              onClick={() => setShowTimeoutDropdown(showTimeoutDropdown === viewer.username ? null : viewer.username)}
                              title="Timeout"
                            >
                              <i className="fas fa-clock"></i>
                            </button>
                            {showTimeoutDropdown === viewer.username && (
                              <div className="timeout-dropdown">
                                <button onClick={() => timeoutUser(viewer.username, 1)}>1 min</button>
                                <button onClick={() => timeoutUser(viewer.username, 5)}>5 min</button>
                                <button onClick={() => timeoutUser(viewer.username, 10)}>10 min</button>
                                <button onClick={() => timeoutUser(viewer.username, 30)}>30 min</button>
                                <button onClick={() => timeoutUser(viewer.username, 60)}>1 hour</button>
                              </div>
                            )}
                          </div>
                          
                          <button 
                            className="action-btn danger"
                            onClick={() => deleteUserMessages(viewer.username)}
                            title="Delete all messages"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Chat FAB */}
      {isMobile && !isChatOpen && (
        <button className="chat-fab" onClick={handleToggleChat}>
          <i className="fas fa-comments"></i>
          {unreadCount > 0 && <span className="fab-badge">{unreadCount}</span>}
        </button>
      )}

      {/* ==================== STYLES ==================== */}
      <style>{`
        /* ===== BASE ===== */
        .streamer-page {
          height: 100vh;
          max-height: 100vh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #0a0a15 0%, #12121f 50%, #0a0a15 100%);
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow: hidden;
        }

        .streamer-page.fullscreen {
          position: fixed;
          inset: 0;
          z-index: 9999;
        }

        /* ===== NOTIFICATIONS ===== */
        .notifications-container {
          position: fixed;
          top: 80px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-width: 280px;
        }

        .notification {
          padding: 10px 16px;
          background: rgba(30, 30, 50, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 10px;
          border-left: 3px solid #e50914;
          box-shadow: 0 8px 30px rgba(0,0,0,0.3);
          animation: slideIn 0.3s ease, fadeOut 0.3s ease 4.7s forwards;
          font-size: 0.85rem;
        }

        .notification.success { border-left-color: #00d26a; }
        .notification.error { border-left-color: #ff4757; }
        .notification.gift { border-left-color: #ffd700; background: linear-gradient(135deg, rgba(255,215,0,0.15), rgba(30,30,50,0.95)); }

        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeOut {
          to { opacity: 0; transform: translateX(100px); }
        }

        /* ===== TOP NAV ===== */
        .top-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          background: #000000;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
          height: 56px;
          min-height: 56px;
          z-index: 100;
        }

        .nav-left, .nav-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: white;
          font-weight: 800;
        }

        .logo img {
          height: 40px;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .logo-title {
          font-weight: 900;
          font-size: 1.125rem;
          letter-spacing: -0.5px;
          line-height: 1.1;
        }

        .logo-title .text-white {
          color: white;
        }

        .logo-title .text-red {
          color: #e50914;
        }

        .logo-subtitle {
          font-size: 0.5rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          color: white;
          opacity: 0.9;
        }

        @media (min-width: 640px) {
          .logo img {
            height: 48px;
          }
          .logo-title {
            font-size: 1.25rem;
          }
          .logo-subtitle {
            font-size: 0.625rem;
          }
        }

        @media (min-width: 1024px) {
          .logo img {
            height: 56px;
          }
          .logo-title {
            font-size: 1.5rem;
          }
          .logo-subtitle {
            font-size: 0.75rem;
          }
        }

        .live-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.5);
          animation: liveGlow 2s ease-in-out infinite;
        }

        @keyframes liveGlow {
          0%, 100% { box-shadow: 0 4px 20px rgba(229, 9, 20, 0.5); }
          50% { box-shadow: 0 4px 35px rgba(229, 9, 20, 0.8); }
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }

        .duration {
          font-variant-numeric: tabular-nums;
          opacity: 0.9;
        }

        .nav-center {
          flex: 1;
          max-width: 550px;
          margin: 0 32px;
        }

        .title-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .title-wrapper:focus-within {
          background: rgba(255,255,255,0.06);
          border-color: rgba(229, 9, 20, 0.5);
          box-shadow: 0 0 25px rgba(229, 9, 20, 0.15);
        }

        .title-wrapper i {
          color: rgba(255,255,255,0.4);
          font-size: 0.85rem;
        }

        .title-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          font-size: 0.95rem;
          outline: none;
        }

        .title-wrapper input::placeholder {
          color: rgba(255,255,255,0.35);
        }

        .title-wrapper input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.04);
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .stat-item.clickable {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .stat-item.clickable:hover {
          background: rgba(229, 9, 20, 0.15);
          transform: scale(1.05);
        }

        .stat-item i {
          color: #e50914;
        }

        .stat-item.peak i {
          color: #00d26a;
        }

        /* Viewers Modal */
        .viewers-modal {
          max-width: 400px;
        }

        .no-viewers {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 40px 20px;
          color: rgba(255,255,255,0.4);
        }

        .no-viewers i {
          font-size: 2.5rem;
        }

        .viewers-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 400px;
          overflow-y: auto;
        }

        .viewer-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: rgba(255,255,255,0.04);
          border-radius: 12px;
          transition: all 0.2s;
          cursor: pointer;
          flex-wrap: wrap;
        }

        .viewer-item:hover {
          background: rgba(255,255,255,0.08);
        }

        .viewer-item.highlighted {
          background: rgba(255, 215, 0, 0.15);
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .viewer-item.timed-out {
          opacity: 0.6;
        }

        .viewer-avatar {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #b20710);
          border-radius: 50%;
          font-size: 0.9rem;
          flex-shrink: 0;
        }

        .viewer-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
        }

        .viewer-name {
          font-weight: 500;
          color: rgba(255,255,255,0.9);
        }

        .badge {
          font-size: 0.75rem;
        }

        .viewer-actions {
          display: flex;
          gap: 8px;
          width: 100%;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        .viewer-actions .action-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .viewer-actions .action-btn:hover {
          background: rgba(255,255,255,0.15);
          color: white;
        }

        .viewer-actions .action-btn.active {
          background: rgba(255, 215, 0, 0.3);
          color: #ffd700;
        }

        .viewer-actions .action-btn.danger:hover {
          background: rgba(229, 9, 20, 0.3);
          color: #e50914;
        }

        .timeout-wrapper {
          position: relative;
        }

        .timeout-dropdown {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1a1a2e;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 10px;
          padding: 6px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 90px;
          margin-bottom: 8px;
          z-index: 100;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        }

        .timeout-dropdown button {
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: rgba(255,255,255,0.8);
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          text-align: center;
        }

        .timeout-dropdown button:hover {
          background: rgba(229, 9, 20, 0.3);
          color: white;
        }

        .nav-btn {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          color: rgba(255,255,255,0.7);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .nav-btn:hover {
          background: rgba(255,255,255,0.08);
          color: white;
          transform: translateY(-2px);
        }

        .nav-btn.chat-btn.has-unread {
          border-color: #e50914;
        }

        .nav-btn .badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e50914;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 700;
        }

        /* ===== MAIN CONTENT ===== */
        .main-content {
          flex: 1;
          display: flex;
          min-height: 0;
          height: 0;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .main-content.with-chat .video-section {
          flex: 0 0 75%;
        }

        /* Mobile Layout - Video on top, Chat below (always visible like YouTube) */
        /* Uses Visual Viewport API for bulletproof iOS keyboard handling */
        .main-content.mobile-layout {
          position: fixed;
          top: 56px;
          left: 0;
          right: 0;
          bottom: 0;
          height: calc(var(--visible-height, 100vh) - 56px);
          flex-direction: column;
          display: flex;
          overflow: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }

        /* Video section - FIXED height, NEVER moves */
        .main-content.mobile-layout .video-section {
          flex: 0 0 200px !important;
          height: 200px !important;
          min-height: 200px !important;
          max-height: 200px !important;
          display: flex;
          flex-direction: column;
          position: relative;
          background: #000;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }

        .main-content.mobile-layout .video-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .main-content.mobile-layout .preview-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .main-content.mobile-layout .camera-off {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%);
          gap: 8px;
        }
        
        .main-content.mobile-layout .camera-off .camera-icon {
          width: 50px;
          height: 50px;
          font-size: 1.3rem;
          background: rgba(229, 9, 20, 0.1);
          border: 1px solid rgba(229, 9, 20, 0.3);
          color: rgba(229, 9, 20, 0.8);
        }
        
        .main-content.mobile-layout .camera-off p {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }

        /* Chat section - FLEX-GROW to fill remaining space */
        .main-content.mobile-layout .chat-section.mobile {
          position: relative !important;
          transform: none !important;
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          border-radius: 0;
          box-shadow: none;
          border-top: 1px solid rgba(255,255,255,0.1);
          display: flex !important;
          flex-direction: column;
          overflow: hidden;
          background: rgba(18, 18, 30, 0.98);
        }
        
        .main-content.mobile-layout .chat-section.mobile .chat-header {
          flex: 0 0 auto;
          padding: 10px 12px;
        }
        
        /* Chat messages - scrollable, flex-grow */
        .main-content.mobile-layout .chat-section.mobile .chat-messages {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        
        .main-content.mobile-layout .chat-section.mobile .reactions-bar {
          flex: 0 0 auto;
        }
        
        /* Input - stays at bottom, sits above keyboard */
        .main-content.mobile-layout .chat-section.mobile .chat-form {
          flex: 0 0 auto;
          padding: 8px 12px;
          padding-bottom: 8px;
          background: #1a1a2e;
          flex-shrink: 0;
        }

        /* When keyboard is open - hide non-essential UI to save space */
        .main-content.mobile-layout.keyboard-open .chat-header {
          display: none !important;
        }
        
        .main-content.mobile-layout.keyboard-open .reactions-bar {
          display: none !important;
        }
        
        .main-content.mobile-layout.keyboard-open .chat-form {
          padding: 6px 10px !important;
          padding-bottom: 6px !important;
          background: #1a1a2e !important;
        }
        
        /* Ensure chat-form never gets cut off */
        .main-content.mobile-layout.keyboard-open .chat-section.mobile {
          overflow: visible;
        }

        /* Mobile video controls - fixed at bottom of video */
        .main-content.mobile-layout .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.9));
          padding: 12px 16px;
          z-index: 10;
        }
        
        /* Hide stream-info on mobile */
        .main-content.mobile-layout .stream-info {
          display: none;
        }

        .video-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
        }

        .video-container {
          flex: 1;
          position: relative;
          background: #000;
          overflow: hidden;
          min-height: 0;
        }

        /* ===== CUSTOM FULLSCREEN PLAYER - iOS Native ===== */
        .video-container.custom-fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          width: 100dvw !important;
          height: 100vh !important;
          height: 100dvh !important;
          z-index: 99999 !important;
          background: #000 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .video-container.custom-fullscreen .preview-video {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
        }

        /* iOS Portrait - rotate to landscape */
        @media screen and (max-width: 932px) and (orientation: portrait) {
          .video-container.custom-fullscreen {
            width: 100vh !important;
            height: 100vw !important;
            transform: rotate(90deg);
            transform-origin: top left;
            left: 100vw !important;
            top: 0 !important;
          }
        }

        /* Landscape - normal */
        @media screen and (orientation: landscape) {
          .video-container.custom-fullscreen {
            transform: none !important;
            left: 0 !important;
          }
        }

        .video-container.custom-fullscreen .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 100000;
          padding: 16px 20px;
          padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
          background: linear-gradient(transparent, rgba(0,0,0,0.9));
        }

        .video-container.custom-fullscreen .fullscreen-chat-overlay {
          z-index: 100000;
          bottom: 100px;
        }

        .video-container.custom-fullscreen .live-overlay {
          z-index: 100000;
        }

        /* Fullscreen Exit Button */
        .fullscreen-exit-btn {
          position: absolute;
          top: env(safe-area-inset-top, 20px);
          right: 20px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.7);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100001;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition: all 0.2s ease;
        }

        .fullscreen-exit-btn:hover,
        .fullscreen-exit-btn:active {
          background: rgba(229, 9, 20, 0.8);
          border-color: #e50914;
          transform: scale(1.1);
        }

        /* Hide nav and chat when fullscreen */
        body.fullscreen-active .top-nav {
          display: none !important;
        }
        
        body.fullscreen-active .chat-section {
          display: none !important;
        }
        
        body.fullscreen-active .stream-info {
          display: none !important;
        }
        
        body.fullscreen-active {
          overflow: hidden !important;
        }

        .preview-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: opacity 0.3s;
        }
        
        /* Hide iOS native video controls and play button */
        .preview-video::-webkit-media-controls {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-enclosure {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-panel {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-start-playback-button {
          display: none !important;
          -webkit-appearance: none;
        }

        .preview-video.hidden {
          opacity: 0;
        }

        .preview-video.with-screen-share {
          position: absolute;
          bottom: 20px;
          right: 20px;
          width: 200px;
          height: 150px;
          border-radius: 12px;
          border: 2px solid rgba(255,255,255,0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          z-index: 10;
        }

        .screen-share-mirror {
          position: absolute;
          inset: 0;
          z-index: 5;
        }

        .screen-share-mirror video {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #000;
        }

        .mirror-label {
          position: absolute;
          top: 16px;
          left: 16px;
          padding: 8px 16px;
          background: rgba(229, 9, 20, 0.9);
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .camera-off {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #15152a, #0a0a15);
          gap: 16px;
        }

        .camera-icon {
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.03);
          border: 2px solid rgba(255,255,255,0.08);
          border-radius: 50%;
          font-size: 3rem;
          color: rgba(255,255,255,0.2);
        }

        .camera-off p {
          color: rgba(255,255,255,0.4);
          font-size: 1.1rem;
        }

        /* Fullscreen Chat Overlay - Twitch/YouTube style */
        .fullscreen-chat-overlay {
          position: absolute;
          bottom: 80px;
          left: 20px;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 100;
          pointer-events: none;
        }

        .overlay-message {
          display: flex;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          border-radius: 20px;
          animation: messageSlideIn 0.3s ease-out, messageFadeOut 0.5s ease-in 4.5s forwards;
          max-width: 100%;
        }

        .overlay-username {
          font-weight: 700;
          color: #e50914;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .overlay-text {
          color: white;
          font-size: 0.9rem;
          word-break: break-word;
        }

        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes messageFadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        .thumbnail-preview {
          position: absolute;
          bottom: 80px;
          right: 20px;
          width: 200px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
        }

        .thumbnail-preview img {
          width: 100%;
          display: block;
        }

        .thumbnail-preview span {
          display: block;
          padding: 8px;
          background: rgba(0,0,0,0.8);
          font-size: 0.75rem;
          text-align: center;
        }

        /* ===== LIVE OVERLAY ===== */
        .live-overlay {
          position: absolute;
          top: 16px;
          left: 16px;
          right: 16px;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
        }

        .overlay-left, .overlay-right {
          display: flex;
          gap: 8px;
        }

        .live-overlay .stat {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(12px);
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .live-overlay .stat.live-badge {
          background: rgba(229, 9, 20, 0.95);
        }

        .live-overlay .stat.screen-share {
          background: rgba(229, 9, 20, 0.9);
        }

        .live-overlay .stat .dot {
          width: 6px;
          height: 6px;
          background: white;
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        /* ===== VIDEO CONTROLS ===== */
        .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: linear-gradient(transparent, rgba(0,0,0,0.95));
          flex-shrink: 0;
          z-index: 250;
        }

        .controls-left, .controls-center, .controls-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ctrl-btn {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 50%;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .ctrl-btn.small {
          width: 36px;
          height: 36px;
          font-size: 0.85rem;
        }

        .ctrl-btn:hover {
          background: rgba(255,255,255,0.15);
          transform: scale(1.08);
        }

        .ctrl-btn.off {
          background: rgba(229, 9, 20, 0.85);
          border-color: rgba(229, 9, 20, 0.5);
        }

        .ctrl-btn.active {
          background: rgba(229, 9, 20, 0.85);
          border-color: rgba(229, 9, 20, 0.5);
        }

        .volume-control {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 14px 6px 6px;
          background: rgba(0,0,0,0.5);
          border-radius: 26px;
        }

        .volume-slider {
          width: 100px;
          height: 4px;
          -webkit-appearance: none;
          background: rgba(255,255,255,0.2);
          border-radius: 2px;
          cursor: pointer;
        }

        .volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: transform 0.2s;
        }

        .volume-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }

        .go-live-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.4);
          white-space: nowrap;
        }

        .go-live-btn:hover:not(:disabled) {
          transform: scale(1.03) translateY(-1px);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.5);
        }

        .go-live-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .end-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.4);
          white-space: nowrap;
        }

        .end-btn:hover {
          transform: scale(1.03);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.5);
        }

        .more-menu-wrapper {
          position: relative;
        }

        .more-menu {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 10px;
          min-width: 180px;
          background: rgba(25, 25, 40, 0.98);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0,0,0,0.5);
          animation: menuSlide 0.2s ease;
        }

        @keyframes menuSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .more-menu button {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 14px 18px;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.85);
          font-size: 0.9rem;
          cursor: pointer;
          transition: background 0.2s;
          text-align: left;
        }

        .more-menu button:hover {
          background: rgba(255,255,255,0.08);
        }

        .more-menu button i {
          width: 18px;
          color: #e50914;
        }

        /* ===== ERROR BANNER ===== */
        .error-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 20px;
          background: linear-gradient(135deg, rgba(229, 9, 20, 0.95), rgba(180, 0, 0, 0.95));
          color: white;
          font-size: 0.9rem;
        }

        .error-banner button {
          margin-left: auto;
          background: none;
          border: none;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          padding: 4px;
        }

        .error-banner button:hover {
          color: white;
        }

        /* ===== STREAM INFO BAR ===== */
        .stream-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          background: rgba(15, 15, 25, 0.98);
          border-top: 1px solid rgba(255,255,255,0.05);
          flex-shrink: 0;
          position: relative;
          z-index: 250;
        }

        .streamer-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .streamer-info .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e50914;
          box-shadow: 0 0 15px rgba(229, 9, 20, 0.35);
        }

        .info-text h3 {
          font-size: 0.9rem;
          font-weight: 700;
          margin: 0 0 2px 0;
        }

        .info-text .status {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
        }

        .info-text .status.live {
          color: #e50914;
        }

        .stream-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: rgba(255,255,255,0.8);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        /* ===== CHAT SECTION ===== */
        .chat-section {
          width: 25%;
          min-width: 320px;
          max-width: 400px;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: rgba(18, 18, 30, 0.98);
          border-left: 1px solid rgba(255,255,255,0.05);
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .chat-section.closed {
          width: 0;
          min-width: 0;
          overflow: hidden;
          border: none;
        }

        .chat-section.floating {
          position: fixed;
          top: 70px;
          right: 20px;
          bottom: 120px;
          width: 380px;
          max-width: 90vw;
          max-height: calc(100vh - 200px);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 25px 80px rgba(0,0,0,0.5);
          z-index: 200;
          display: flex;
          flex-direction: column;
        }

        .chat-section.floating .chat-messages {
          flex: 1;
          min-height: 0;
          max-height: calc(100vh - 420px);
        }

        .chat-section.floating .chat-form {
          flex-shrink: 0;
        }

        /* Mobile chat - uses flex layout from mobile-layout, but keep base styles for non-mobile-layout fallback */
        .chat-section.mobile {
          position: relative;
          width: 100%;
          max-width: 100%;
          z-index: 50;
        }

        .chat-section.mobile.open {
          /* No transform needed for flex layout */
        }

        .chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          background: rgba(229, 9, 20, 0.08);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .chat-tabs {
          display: flex;
          gap: 4px;
        }

        .chat-tabs .tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.5);
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .chat-tabs .tab.active {
          background: rgba(229, 9, 20, 0.15);
          color: #e50914;
        }

        .chat-tabs .tab:hover:not(.active) {
          color: rgba(255,255,255,0.8);
        }

        .chat-actions {
          display: flex;
          gap: 6px;
        }

        .small-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.5);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .small-btn:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        .small-btn.active {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .small-btn.close:hover {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }

        .chat-empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: rgba(255,255,255,0.25);
          gap: 8px;
        }

        .chat-empty i {
          font-size: 3.5rem;
          opacity: 0.4;
          margin-bottom: 8px;
        }

        .chat-empty p {
          font-size: 1.05rem;
          margin: 0;
        }

        .chat-empty span {
          font-size: 0.85rem;
        }

        .message {
          position: relative;
          padding: 12px 14px;
          background: rgba(255,255,255,0.03);
          border-radius: 12px;
          animation: msgIn 0.3s ease;
        }

        @keyframes msgIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .message.streamer {
          background: linear-gradient(135deg, rgba(229, 9, 20, 0.12), rgba(229, 9, 20, 0.05));
          border: 1px solid rgba(229, 9, 20, 0.2);
        }

        .message.highlighted {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.05));
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .message.user-highlighted {
          background: linear-gradient(135deg, rgba(0, 210, 106, 0.15), rgba(0, 210, 106, 0.05));
          border-left: 3px solid #00d26a;
        }

        .msg-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }

        .msg-badges {
          display: flex;
          gap: 4px;
        }

        .badge-icon {
          font-size: 0.8rem;
        }

        .badge-icon.highlight {
          animation: sparkle 1.5s ease-in-out infinite;
        }

        @keyframes sparkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .msg-author {
          font-weight: 700;
          color: #e50914;
          font-size: 0.9rem;
        }

        .msg-author.highlighted-user {
          color: #00d26a;
          text-shadow: 0 0 10px rgba(0, 210, 106, 0.3);
        }

        .message.streamer .msg-author {
          color: #ff6b9d;
        }

        .msg-actions button i.active {
          color: #00d26a;
        }

        .msg-time {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.3);
          margin-left: auto;
        }

        .msg-content {
          font-size: 0.9rem;
          line-height: 1.5;
          color: rgba(255,255,255,0.9);
          word-break: break-word;
        }

        .msg-actions {
          position: absolute;
          top: 8px;
          right: 8px;
          display: none;
          gap: 4px;
        }

        .message:hover .msg-actions {
          display: flex;
        }

        .msg-actions button {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 6px;
          color: rgba(255,255,255,0.6);
          font-size: 0.7rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .msg-actions button:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        /* ===== REACTIONS BAR ===== */
        .reactions-bar {
          display: flex;
          justify-content: center;
          gap: 6px;
          padding: 10px;
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .reaction-btn {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.04);
          border: none;
          border-radius: 10px;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .reaction-btn:hover {
          background: rgba(255,255,255,0.1);
          transform: scale(1.15);
        }

        /* ===== CHAT INPUT ===== */
        .chat-form {
          padding: 14px;
          background: rgba(0,0,0,0.3);
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .input-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          transition: all 0.2s;
        }

        .input-wrapper:focus-within {
          border-color: rgba(229, 9, 20, 0.5);
        }

        .emoji-btn {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 10px;
          color: rgba(255,255,255,0.5);
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emoji-btn:hover {
          background: rgba(255,255,255,0.08);
          color: white;
        }

        .input-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          font-size: 0.95rem;
          outline: none;
        }

        .input-wrapper input::placeholder {
          color: rgba(255,255,255,0.35);
        }

        .send-btn {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .send-btn:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 18px rgba(229, 9, 20, 0.4);
        }

        .send-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .emoji-picker {
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 6px;
          padding: 12px;
          margin-top: 10px;
          background: rgba(0,0,0,0.4);
          border-radius: 12px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .emoji-picker button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 8px;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emoji-picker button:hover {
          background: rgba(255,255,255,0.12);
          transform: scale(1.1);
        }

        /* ===== MODALS ===== */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          animation: fadeIn 0.2s ease;
          overflow-y: auto;
        }

        .modal {
          width: 100%;
          max-width: 500px;
          max-height: 85vh;
          overflow-y: auto;
          background: linear-gradient(135deg, #1a1a2e, #15152a);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          box-shadow: 0 30px 100px rgba(0,0,0,0.6);
          animation: modalIn 0.3s ease;
        }

        /* Mobile modal - full screen */
        @media (max-width: 600px) {
          .modal-overlay {
            padding: 0;
            align-items: flex-start;
          }
          
          .modal {
            max-width: 100%;
            max-height: 100vh;
            min-height: 100vh;
            height: 100vh;
            border-radius: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
          }
          
          .modal-header {
            padding: 16px;
            flex-shrink: 0;
            background: linear-gradient(135deg, #1a1a2e, #15152a);
            border-bottom: 1px solid rgba(255,255,255,0.1);
          }
          
          .modal-content {
            padding: 16px;
            padding-bottom: 100px;
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .modal-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 16px;
            background: linear-gradient(transparent, #1a1a2e 20%);
            z-index: 10;
          }
        }

        .settings-modal {
          min-height: 520px;
          max-height: 85vh;
        }
        
        @media (max-width: 600px) {
          .settings-modal {
            min-height: 100vh;
            max-height: 100vh;
          }
        }

        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: rgba(229, 9, 20, 0.08);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .modal-header h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.2rem;
          margin: 0;
        }

        .modal-header h2 i {
          color: #e50914;
        }

        .modal-header button {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 10px;
          color: rgba(255,255,255,0.6);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modal-header button:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        .modal-content {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          background: rgba(0,0,0,0.2);
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .setting-group {
          margin-bottom: 24px;
        }

        .setting-group label {
          display: block;
          margin-bottom: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
        }

        .setting-group small {
          display: block;
          margin-top: 8px;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .setting-group input[type="text"],
        .setting-group input[type="password"],
        .setting-group select {
          width: 100%;
          padding: 14px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
          outline: none;
          transition: all 0.2s;
        }

        .setting-group input:focus,
        .setting-group select:focus {
          border-color: rgba(229, 9, 20, 0.5);
        }

        .setting-group select option {
          background: #1a1a2e;
        }

        .key-input {
          display: flex;
          gap: 8px;
        }

        .key-input input {
          flex: 1;
        }

        .key-input button {
          width: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .key-input button:hover {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .settings-row {
          display: flex;
          gap: 16px;
        }

        .setting-group.half {
          flex: 1;
        }

        .thumbnail-upload {
          display: flex;
          justify-content: center;
        }

        .upload-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 30px 50px;
          background: rgba(255,255,255,0.04);
          border: 2px dashed rgba(255,255,255,0.15);
          border-radius: 16px;
          color: rgba(255,255,255,0.5);
          cursor: pointer;
          transition: all 0.3s;
        }

        .upload-btn:hover {
          background: rgba(229, 9, 20, 0.08);
          border-color: rgba(229, 9, 20, 0.4);
          color: #e50914;
        }

        .upload-btn i {
          font-size: 2.5rem;
        }

        .thumb-preview {
          position: relative;
          width: 200px;
          border-radius: 12px;
          overflow: hidden;
        }

        .thumb-preview img {
          width: 100%;
          display: block;
        }

        .thumb-preview button {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.8);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
        }

        .range-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .range-labels .current {
          color: #e50914;
          font-weight: 600;
        }

        /* Source Toggle */
        .source-toggle {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .source-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px 16px;
          background: rgba(255,255,255,0.05);
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .source-btn:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.2);
          color: white;
        }

        .source-btn.active {
          background: rgba(229, 9, 20, 0.15);
          border-color: #e50914;
          color: white;
        }

        .source-btn i {
          font-size: 1.8rem;
        }

        .source-btn span {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .source-btn small {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
        }

        .source-btn.active small {
          color: rgba(229, 9, 20, 0.8);
        }

        /* OBS Instructions */
        .obs-instructions {
          padding: 16px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          margin-top: 16px;
        }

        .obs-instructions h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 12px 0;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.9);
        }

        .obs-instructions h4 i {
          color: #e50914;
        }

        .obs-instructions ol {
          margin: 0;
          padding-left: 20px;
        }

        .obs-instructions li {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.7);
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .obs-instructions code {
          display: inline-block;
          padding: 2px 8px;
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.8rem;
          color: #e50914;
        }

        .warning-text {
          display: block;
          margin-top: 8px;
          padding: 10px;
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          color: #ffc107;
          font-size: 0.8rem;
        }

        .upload-btn small {
          display: block;
          margin-top: 4px;
          font-size: 0.75rem;
          color: rgba(255,255,255,0.4);
        }

        .setting-group input[type="range"] {
          width: 100%;
          height: 6px;
          -webkit-appearance: none;
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
          cursor: pointer;
        }

        .setting-group input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          background: #e50914;
          border-radius: 50%;
          box-shadow: 0 2px 10px rgba(229, 9, 20, 0.4);
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn.primary {
          background: linear-gradient(135deg, #e50914, #b20710);
          color: white;
          box-shadow: 0 4px 15px rgba(229, 9, 20, 0.3);
        }

        .btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.4);
        }

        .btn.secondary {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.8);
        }

        .btn.secondary:hover {
          background: rgba(255,255,255,0.12);
          color: white;
        }

        /* ===== SETTINGS MODAL SPECIFIC ===== */
        .setting-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .setting-item:last-child {
          border-bottom: none;
        }

        .setting-info span {
          display: block;
          font-size: 0.95rem;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .setting-info small {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .toggle {
          position: relative;
          width: 54px;
          height: 30px;
          cursor: pointer;
        }

        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle .slider {
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.1);
          border-radius: 15px;
          transition: 0.3s;
        }

        .toggle .slider:before {
          content: "";
          position: absolute;
          width: 24px;
          height: 24px;
          left: 3px;
          bottom: 3px;
          background: white;
          border-radius: 50%;
          transition: 0.3s;
        }

        .toggle input:checked + .slider {
          background: linear-gradient(135deg, #e50914, #b20710);
        }

        .toggle input:checked + .slider:before {
          transform: translateX(24px);
        }

        .shortcuts-section {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .shortcuts-section h4 {
          font-size: 0.95rem;
          margin: 0 0 16px 0;
          color: rgba(255,255,255,0.6);
        }

        .shortcut {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.7);
        }

        .shortcut kbd {
          min-width: 32px;
          padding: 6px 10px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          font-family: inherit;
          font-size: 0.8rem;
          text-align: center;
        }

        /* ===== MOBILE FAB ===== */
        .chat-fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 1.6rem;
          cursor: pointer;
          box-shadow: 0 8px 35px rgba(229, 9, 20, 0.5);
          z-index: 100;
          transition: all 0.3s;
        }

        .chat-fab:hover {
          transform: scale(1.1);
        }

        .fab-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e50914;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 700;
        }

        /* ============================================ */
        /* RESPONSIVE DESIGN - ALL DEVICES */
        /* ============================================ */

        /* Large Desktop & TV (1920px+) */
        @media (min-width: 1920px) {
          .top-nav {
            padding: 16px 32px;
          }
          
          .logo img {
            height: 42px;
          }
          
          .logo span {
            font-size: 1.3rem;
          }
          
          .stat-item {
            padding: 10px 18px;
            font-size: 0.95rem;
          }
          
          .nav-btn {
            width: 52px;
            height: 52px;
          }
          
          .title-wrapper input {
            font-size: 1.05rem;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 78%;
          }
          
          .chat-section {
            width: 22%;
            max-width: 450px;
          }
          
          .ctrl-btn {
            width: 58px;
            height: 58px;
            font-size: 1.25rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 14px 32px;
            font-size: 1.05rem;
          }
          
          .stream-info {
            padding: 20px 28px;
          }
          
          .streamer-info .avatar {
            width: 58px;
            height: 58px;
          }
          
          .info-text h3 {
            font-size: 1.15rem;
          }
        }

        /* Desktop (1200px - 1919px) */
        @media (min-width: 1200px) and (max-width: 1919px) {
          .main-content.with-chat .video-section {
            flex: 0 0 76%;
          }
          
          .chat-section {
            width: 24%;
            max-width: 420px;
          }
        }

        /* Laptop (992px - 1199px) */
        @media (min-width: 992px) and (max-width: 1199px) {
          .top-nav {
            padding: 10px 20px;
          }
          
          .nav-center {
            margin: 0 20px;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 72%;
          }
          
          .chat-section {
            width: 28%;
            min-width: 280px;
            max-width: 360px;
          }
        }

        /* Tablet Landscape (768px - 991px) */
        @media (min-width: 768px) and (max-width: 991px) {
          .top-nav {
            padding: 10px 16px;
          }
          
          .logo img {
            height: 30px;
          }
          
          .logo span {
            font-size: 0.95rem;
          }
          
          .nav-center {
            margin: 0 16px;
            max-width: 350px;
          }
          
          .title-wrapper {
            padding: 8px 12px;
          }
          
          .title-wrapper input {
            font-size: 0.85rem;
          }
          
          .stat-item {
            padding: 6px 10px;
            font-size: 0.8rem;
          }
          
          .nav-btn {
            width: 40px;
            height: 40px;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 65%;
          }
          
          .chat-section {
            width: 35%;
            min-width: 260px;
            max-width: 320px;
          }
          
          .ctrl-btn {
            width: 48px;
            height: 48px;
            font-size: 1.05rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 10px 22px;
            font-size: 0.9rem;
          }
          
          .stream-info {
            padding: 12px 16px;
          }
          
          .streamer-info .avatar {
            width: 44px;
            height: 44px;
          }
          
          .action-btn {
            padding: 8px 14px;
            font-size: 0.85rem;
          }
        }

        /* Tablet Portrait & Large Phones (576px - 767px) */
        @media (min-width: 576px) and (max-width: 767px) {
          .top-nav {
            padding: 8px 12px;
          }

          .logo img {
            height: 28px;
          }
          
          .logo span {
            font-size: 0.85rem;
          }

          .nav-center {
            display: none;
          }
          
          .stat-item {
            padding: 5px 8px;
            font-size: 0.75rem;
          }
          
          .nav-btn {
            width: 38px;
            height: 38px;
            font-size: 0.9rem;
          }

          .main-content.with-chat .video-section {
            flex: 1;
          }

          .video-controls {
            padding: 12px 14px;
          }

          .ctrl-btn {
            width: 44px;
            height: 44px;
            font-size: 0.95rem;
          }

          .go-live-btn, .end-btn {
            padding: 10px 20px;
            font-size: 0.9rem;
          }

          .volume-control {
            display: none;
          }

          .stream-info {
            padding: 10px 12px;
          }
          
          .streamer-info .avatar {
            width: 40px;
            height: 40px;
          }
          
          .info-text h3 {
            font-size: 0.95rem;
          }
          
          .info-text .status {
            font-size: 0.8rem;
          }

          .action-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
          
          .action-btn span {
            display: none;
          }
          
          .chat-section {
            width: 100%;
            max-width: 100%;
          }

          .modal {
            max-width: 95%;
            margin: 10px;
            border-radius: 16px;
            max-height: 90vh;
          }
          
          .chat-fab {
            width: 56px;
            height: 56px;
            font-size: 1.4rem;
            bottom: 20px;
            right: 20px;
          }
        }

        /* Mobile Phones (320px - 575px) */
        @media (max-width: 575px) {
          .top-nav {
            padding: 8px 10px;
          }

          .logo img {
            height: 26px;
          }
          
          .logo-title {
            font-size: 0.85rem;
          }
          
          .logo-subtitle {
            font-size: 0.4rem;
          }

          .nav-center {
            display: none;
          }
          
          .live-indicator {
            padding: 4px 10px;
            font-size: 0.65rem;
          }
          
          .live-indicator .duration {
            display: none;
          }
          
          .stat-item {
            padding: 4px 8px;
            font-size: 0.7rem;
          }
          
          .stat-item.peak {
            display: none;
          }
          
          .nav-btn {
            width: 36px;
            height: 36px;
            font-size: 0.85rem;
            border-radius: 10px;
          }

          .main-content.with-chat .video-section {
            flex: 1;
          }

          .video-controls {
            padding: 10px 12px;
            gap: 8px;
          }
          
          .controls-left {
            gap: 6px;
          }
          
          .controls-right {
            gap: 6px;
          }

          .ctrl-btn {
            width: 40px;
            height: 40px;
            font-size: 0.9rem;
          }
          
          .ctrl-btn.small {
            width: 34px;
            height: 34px;
            font-size: 0.8rem;
          }

          .go-live-btn, .end-btn {
            padding: 10px 18px;
            font-size: 0.85rem;
          }
          
          .go-live-btn span, .end-btn span {
            display: none;
          }
          
          .go-live-btn i, .end-btn i {
            margin: 0;
          }

          .volume-control {
            display: none;
          }

          .stream-info {
            padding: 10px;
            flex-wrap: wrap;
          }
          
          .streamer-info {
            gap: 10px;
          }
          
          .streamer-info .avatar {
            width: 36px;
            height: 36px;
          }
          
          .info-text h3 {
            font-size: 0.9rem;
          }
          
          .info-text .status {
            font-size: 0.75rem;
          }

          .stream-actions {
            gap: 6px;
          }
          
          .action-btn {
            padding: 8px 10px;
            font-size: 0.8rem;
          }
          
          .action-btn span {
            display: none;
          }
          
          .chat-section {
            width: 100%;
            max-width: 100%;
            border-radius: 0;
          }
          
          .chat-header {
            padding: 10px 12px;
          }
          
          .chat-tabs .tab {
            padding: 6px 10px;
            font-size: 0.75rem;
          }
          
          .chat-messages {
            padding: 10px;
            gap: 8px;
          }
          
          .message {
            padding: 10px;
            border-radius: 10px;
          }
          
          .msg-author {
            font-size: 0.8rem;
          }
          
          .msg-content {
            font-size: 0.85rem;
          }
          
          .reactions-bar {
            padding: 8px;
            gap: 4px;
          }
          
          .reaction-btn {
            width: 34px;
            height: 34px;
            font-size: 1rem;
          }
          
          .chat-form {
            padding: 10px;
          }
          
          .input-wrapper {
            padding: 4px;
            border-radius: 12px;
          }
          
          .input-wrapper input {
            font-size: 0.85rem;
          }
          
          .emoji-btn {
            width: 34px;
            height: 34px;
            font-size: 0.95rem;
          }
          
          .send-btn {
            width: 38px;
            height: 38px;
            font-size: 0.9rem;
            border-radius: 10px;
          }

          .modal {
            max-width: 100%;
            margin: 0;
            border-radius: 0;
            max-height: 100vh;
          }
          
          .modal-header {
            padding: 14px 16px;
          }
          
          .modal-header h2 {
            font-size: 1rem;
          }
          
          .modal-content {
            padding: 16px;
          }
          
          .modal-footer {
            padding: 14px 16px;
          }
          
          .setting-group label {
            font-size: 0.85rem;
          }
          
          .setting-group input,
          .setting-group select {
            padding: 12px;
            font-size: 0.9rem;
          }
          
          .btn {
            padding: 10px 18px;
            font-size: 0.85rem;
          }
          
          .chat-fab {
            width: 52px;
            height: 52px;
            font-size: 1.3rem;
            bottom: 16px;
            right: 16px;
          }
          
          .fab-badge {
            width: 20px;
            height: 20px;
            font-size: 0.65rem;
          }
          
          .camera-off p {
            font-size: 0.95rem;
          }
          
          .camera-icon {
            width: 90px;
            height: 90px;
            font-size: 2.2rem;
          }
          
          .live-overlay .stat {
            padding: 6px 10px;
            font-size: 0.7rem;
          }
          
          .live-overlay .stat.live-badge {
            padding: 6px 8px;
          }
        }

        /* Very Small Phones (< 375px) */
        @media (max-width: 374px) {
          .top-nav {
            padding: 6px 8px;
          }
          
          .logo img {
            height: 22px;
          }
          
          .live-indicator {
            padding: 3px 8px;
            font-size: 0.6rem;
          }
          
          .stat-item {
            padding: 3px 6px;
            font-size: 0.65rem;
            gap: 4px;
          }
          
          .nav-btn {
            width: 32px;
            height: 32px;
            font-size: 0.8rem;
          }
          
          .ctrl-btn {
            width: 36px;
            height: 36px;
            font-size: 0.85rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 8px 14px;
            font-size: 0.8rem;
          }
          
          .stream-info {
            padding: 8px;
          }
          
          .streamer-info .avatar {
            width: 32px;
            height: 32px;
          }
          
          .info-text h3 {
            font-size: 0.85rem;
          }
          
          .action-btn {
            padding: 6px 8px;
            font-size: 0.75rem;
          }
          
          .chat-fab {
            width: 46px;
            height: 46px;
            font-size: 1.1rem;
            bottom: 12px;
            right: 12px;
          }
        }

        /* Safe Area for iPhone X+ */
        @supports (padding-top: env(safe-area-inset-top)) {
          .top-nav {
            padding-top: calc(8px + env(safe-area-inset-top));
          }
          
          .video-controls {
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          
          .stream-info {
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          
          .chat-fab {
            bottom: calc(16px + env(safe-area-inset-bottom));
          }
          
          .chat-section {
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
      `}</style>
    </div>
  );
}
